import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Link as LinkIcon, Mail, Lock, User, Building2, Phone,
  ArrowRight, ArrowLeft, Check, ChevronDown, Search,
  Users, Briefcase, Megaphone, CreditCard, Shield, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError, partnerInvitesAPI } from '../lib/api';
import { Progress } from '../components/ui/progress';
import { HBCU_LIST } from '../lib/hbcuList';
import api from '../lib/api';

const BACKGROUND_IMAGES = [
  'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1600&q=80', // Woman and man at monitor
  'https://images.unsplash.com/photo-1521790361543-f645cf042ec4?w=1600&q=80', // Hands on macbook flat lay
  'https://images.unsplash.com/photo-1573167627769-e201a7ddf409?w=1600&q=80', // Women at conference table
  'https://images.unsplash.com/photo-1679117349740-c46c819d0373?w=1600&q=80', // Man in suit on phone
  'https://images.unsplash.com/photo-1622842547373-5aa4bf062dc5?w=1600&q=80', // Person in black long sleeve
  'https://images.unsplash.com/photo-1674052353268-6412e0781b4a?w=1600&q=80', // Men sitting on stool
  'https://images.unsplash.com/photo-1709811240710-cff5f04deb44?w=1600&q=80', // Woman in graduation cap
  'https://images.unsplash.com/photo-1764264829056-2775046bcf42?w=1600&q=80', // Woman in suit and sunglasses on rooftop
  'https://images.unsplash.com/photo-1765648684898-2213d6b11c5d?w=1600&q=80', // Woman at outdoor cafe with laptop
  'https://images.unsplash.com/photo-1765648496267-a0b528fb97fb?w=1600&q=80', // Woman typing on laptop at desk
  'https://images.unsplash.com/photo-1616804947838-6646ae0e423d?w=1600&q=80', // Man in blue suit beside black car
  'https://images.unsplash.com/photo-1739289696461-76363e419fae?w=1600&q=80', // Man sitting on chair reading
  'https://images.unsplash.com/photo-1552652893-2aa10a0ab4df?w=1600&q=80', // Man standing near high-rise building
  'https://images.unsplash.com/photo-1615572359976-1fe39507ed7b?w=1600&q=80', // Man in gray shirt near bridge
  'https://images.unsplash.com/photo-1611432579402-7037e3e2c1e4?w=1600&q=80', // Man in yellow blazer smiling
  'https://images.unsplash.com/photo-1589156280159-27698a70f29e?w=1600&q=80', // Woman in white collared shirt city night
  'https://images.unsplash.com/photo-1597711662967-b5dac822f5ac?w=1600&q=80', // March on Washington demonstrators
  'https://images.unsplash.com/photo-1597702693388-c985cfd3eaea?w=1600&q=80', // Reflecting pool March on Washington 1963
  'https://images.unsplash.com/photo-1648009847543-7491343aaec8?w=1600&q=80', // I am Black History t-shirt
  'https://images.unsplash.com/photo-1697183381312-0f2ac034bddc?w=1600&q=80', // Men with signs at US Capitol civil rights
];

const ROLES = [
  {
    id: 'alumni_association',
    title: 'Alumni Association',
    icon: Users,
    description: 'Manage and engage with alumni from your institution. Access member directories, send announcements, track engagement.',
    startingPrice: 99,
  },
  {
    id: 'recruiter',
    title: 'Recruiter',
    icon: Briefcase,
    description: 'Find and recruit top HBCU talent. AI-powered candidate matching, pipeline management, direct messaging.',
    startingPrice: 149,
  },
  {
    id: 'sponsor',
    title: 'Sponsor / Advertiser',
    icon: Megaphone,
    description: 'Reach the HBCU community. Create targeted ad campaigns, sponsored content, and track engagement analytics.',
    startingPrice: 199,
  },
];

const PLANS = {
  alumni_association: [
    {
      id: 'aa_basic',
      name: 'Basic',
      price: 99,
      popular: false,
      features: [
        'Up to 500 members',
        '5 announcements/mo',
        'Basic analytics',
      ],
    },
    {
      id: 'aa_pro',
      name: 'Pro',
      price: 199,
      popular: true,
      features: [
        'Up to 2,000 members',
        'Unlimited announcements',
        'Advanced analytics',
        'CSV export',
      ],
    },
    {
      id: 'aa_enterprise',
      name: 'Enterprise',
      price: 299,
      priceLabel: '$299+',
      popular: false,
      features: [
        'Unlimited members',
        'Custom branding',
        'Dedicated support',
        'API access',
      ],
    },
  ],
  recruiter: [
    {
      id: 'rec_starter',
      name: 'Starter',
      price: 149,
      popular: false,
      features: [
        '5 job posts',
        '50 candidate views/mo',
        'Basic search',
      ],
    },
    {
      id: 'rec_pro',
      name: 'Pro',
      price: 299,
      popular: true,
      features: [
        '25 job posts',
        '500 candidate views/mo',
        'AI matching',
        'Pipeline management',
      ],
    },
    {
      id: 'rec_enterprise',
      name: 'Enterprise',
      price: 499,
      priceLabel: '$499+',
      popular: false,
      features: [
        'Unlimited posts',
        'Unlimited views',
        'Priority support',
        'ATS integration',
      ],
    },
  ],
  sponsor: [
    {
      id: 'sp_starter',
      name: 'Starter',
      price: 199,
      popular: false,
      features: [
        '1 campaign',
        '10K impressions/mo',
        'Basic targeting',
      ],
    },
    {
      id: 'sp_growth',
      name: 'Growth',
      price: 499,
      popular: true,
      features: [
        '5 campaigns',
        '100K impressions/mo',
        'Advanced targeting',
        'A/B testing',
      ],
    },
    {
      id: 'sp_enterprise',
      name: 'Enterprise',
      price: 999,
      priceLabel: '$999+',
      popular: false,
      features: [
        'Unlimited campaigns',
        'Unlimited impressions',
        'Premium placements',
        'Dedicated account manager',
      ],
    },
  ],
};

const STEPS = [
  { title: 'Choose Role', icon: Shield },
  { title: 'Select Plan', icon: CreditCard },
  { title: 'Account', icon: User },
  { title: 'Payment', icon: CreditCard },
];

const RoleSignup = () => {
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Account fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [organization, setOrganization] = useState('');
  const [institution, setInstitution] = useState('');
  const [institutionSearch, setInstitutionSearch] = useState('');
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const [phone, setPhone] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Partner invite (comped access — bypasses plan selection + Stripe)
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [invite, setInvite] = useState(null);

  const [bgImage] = useState(BACKGROUND_IMAGES[Math.floor(Math.random() * BACKGROUND_IMAGES.length)]);

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    partnerInvitesAPI.lookup(inviteToken).then(res => {
      if (cancelled) return;
      const inv = res.data || {};
      if (inv.accepted) { toast.error('This invite has already been used. Please sign in.'); return; }
      if (inv.expired) { toast.error('This invite has expired. Ask your GradLink contact for a new one.'); return; }
      setInvite(inv);
      // Lock role/plan to the invite and jump straight to account creation.
      if (inv.role) setSelectedRole(inv.role);
      if (inv.plan) setSelectedPlan(inv.plan);
      if (inv.email) setEmail(inv.email);
      if (inv.organization) setOrganization(inv.organization);
      if (inv.contact_name) setName(inv.contact_name);
      setStep(3); // skip role + plan selection
    }).catch(() => {
      toast.error('We couldn\'t find that invite. You can still sign up normally below.');
    });
    return () => { cancelled = true; };
  }, [inviteToken]);

  const filteredInstitutions = HBCU_LIST.filter(inst =>
    inst.name.toLowerCase().includes(institutionSearch.toLowerCase()) ||
    inst.state.toLowerCase().includes(institutionSearch.toLowerCase())
  );

  const progressPercent = Math.round((step / STEPS.length) * 100);

  const currentPlans = selectedRole ? PLANS[selectedRole] : [];
  const currentRoleInfo = ROLES.find(r => r.id === selectedRole);
  const currentPlanInfo = currentPlans.find(p => p.id === selectedPlan);

  const handleSelectRole = (roleId) => {
    setSelectedRole(roleId);
    setSelectedPlan(null);
    setStep(2);
  };

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    setStep(3);
  };

  const handleAccountNext = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (!agreedToTerms) { toast.error('Please agree to the Terms of Service'); return; }
    if (selectedRole === 'alumni_association' && !institution) { toast.error('Please select your institution'); return; }
    // Comped invite → no payment step, submit directly
    if (invite) { handleSubscribe(); return; }
    setStep(4);
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await api.post('/auth/role-signup', {
        email,
        password,
        name,
        role: selectedRole,
        plan: selectedPlan,
        organization,
        institution: selectedRole === 'alumni_association' ? institution : undefined,
        phone: phone || undefined,
        origin_url: window.location.origin,
        invite_token: inviteToken || undefined,
      });

      if (response.data?.comped) {
        toast.success(response.data.message || 'Welcome to GradLink! Your access is active.');
        navigate('/login');
      } else if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Account created! Redirecting...');
        navigate('/login');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Background Image (desktop) */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgImage})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-dark-slate via-dark-slate/85 to-dark-slate/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-slate via-dark-slate/40 to-dark-slate/20" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-white/15 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20">
              <LinkIcon className="w-8 h-8 text-white" />
            </div>
            <span className="font-heading font-extrabold text-3xl text-white [text-shadow:_0_2px_8px_rgba(0,0,0,0.5)]">GradLink</span>
          </div>
          <div className="max-w-lg bg-dark-slate/60 backdrop-blur-md rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h2 className="font-heading font-extrabold text-4xl text-white leading-tight mb-4 [text-shadow:_0_2px_12px_rgba(0,0,0,0.7)]">
              Partner with GradLink
            </h2>
            <p className="text-white/90 text-lg [text-shadow:_0_1px_6px_rgba(0,0,0,0.6)]">
              Recruit top HBCU talent, reach the alumni community, or manage your alumni association — all in one platform.
            </p>
          </div>
          <p className="text-white/50 text-xs">Photos from Unsplash</p>
        </div>
      </div>

      {/* Right: Signup Flow */}
      <div className="flex-1 lg:max-w-2xl flex flex-col p-6 bg-white relative overflow-y-auto">
        {/* Mobile background */}
        <div className="absolute inset-0 lg:hidden overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${bgImage})` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/70 to-white/90" />
        </div>

        <div className="relative w-full max-w-lg mx-auto z-10 flex-1 flex flex-col justify-center">
          {/* Mobile logo */}
          <div className="text-center mb-6 lg:hidden">
            <div className="inline-flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-dark-slate rounded-2xl flex items-center justify-center shadow-lg">
                <LinkIcon className="w-6 h-6 text-mint-fresh" />
              </div>
              <h1 className="font-heading font-extrabold text-2xl text-dark-slate">GradLink</h1>
            </div>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-gray font-medium">Step {step} of {STEPS.length}</span>
              <span className="text-xs text-slate-gray">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-1.5" />
            <div className="flex justify-between mt-3">
              {STEPS.map((s, i) => (
                <button key={i} onClick={() => i + 1 < step && setStep(i + 1)}
                  className={i + 1 <= step ? 'cursor-pointer' : 'cursor-default'} disabled={i + 1 > step}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    i + 1 === step ? 'bg-dark-slate text-white' : i + 1 < step ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-gray'
                  }`}>{i + 1 < step ? <Check className="w-3.5 h-3.5" /> : i + 1}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 1: Choose Role */}
          {step === 1 && (
            <div>
              <h2 className="font-heading font-bold text-2xl text-dark-slate text-center mb-2">Choose Your Role</h2>
              <p className="text-slate-gray text-center text-sm mb-6">Select how you want to use GradLink as a partner.</p>
              <div className="space-y-4">
                {ROLES.map(role => {
                  const Icon = role.icon;
                  return (
                    <button key={role.id} onClick={() => handleSelectRole(role.id)}
                      className="w-full text-left p-5 rounded-2xl border-2 border-slate-200 hover:border-dark-slate hover:shadow-md transition-all group bg-white">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 rounded-xl bg-dark-slate/5 flex items-center justify-center flex-shrink-0 group-hover:bg-dark-slate/10 transition-colors">
                          <Icon className="w-6 h-6 text-dark-slate" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-heading font-bold text-lg text-dark-slate">{role.title}</h3>
                            <span className="text-sm font-semibold text-emerald-600">From ${role.startingPrice}/mo</span>
                          </div>
                          <p className="text-slate-gray text-sm leading-relaxed">{role.description}</p>
                        </div>
                      </div>
                      <div className="flex justify-end mt-3">
                        <span className="text-sm font-medium text-dark-slate flex items-center group-hover:translate-x-1 transition-transform">
                          Select <ArrowRight className="w-4 h-4 ml-1" />
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-center text-slate-gray mt-6 text-sm">
                Looking to join as an alumnus? <Link to="/register" className="text-dark-slate font-semibold hover:underline">Sign up here</Link>
              </p>
            </div>
          )}

          {/* Step 2: Select Plan */}
          {step === 2 && (
            <div>
              <h2 className="font-heading font-bold text-2xl text-dark-slate text-center mb-2">
                {currentRoleInfo?.title} Plans
              </h2>
              <p className="text-slate-gray text-center text-sm mb-6">Choose the plan that fits your needs.</p>
              <div className="space-y-4">
                {currentPlans.map(plan => (
                  <button key={plan.id} onClick={() => handleSelectPlan(plan.id)}
                    className={`w-full text-left p-5 rounded-2xl border-2 transition-all bg-white relative ${
                      plan.popular
                        ? 'border-emerald-400 shadow-md hover:shadow-lg'
                        : 'border-slate-200 hover:border-dark-slate hover:shadow-md'
                    }`}>
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">Most Popular</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading font-bold text-lg text-dark-slate">{plan.name}</h3>
                      <div className="text-right">
                        <span className="text-2xl font-extrabold text-dark-slate">{plan.priceLabel || `$${plan.price}`}</span>
                        <span className="text-slate-gray text-sm">/mo</span>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-sm text-slate-gray">
                          <Check className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4">
                      <div className={`w-full h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                        plan.popular
                          ? 'bg-dark-slate text-white'
                          : 'bg-slate-100 text-dark-slate group-hover:bg-dark-slate group-hover:text-white'
                      }`}>
                        Select Plan
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex justify-start mt-6">
                <Button variant="outline" onClick={handleBack} className="h-11 rounded-full px-5">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Account Creation */}
          {step === 3 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 lg:shadow-none lg:border-0 lg:p-0">
              <h2 className="font-heading font-bold text-2xl text-dark-slate text-center mb-6">Create Your Account</h2>

              {invite && (
                <div className="mb-6 rounded-2xl border border-mint-fresh/40 bg-mint-fresh/10 p-5">
                  <div className="flex items-start gap-3">
                    <Gift className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-dark-slate">
                        You're invited as a {invite.role_label} partner — {invite.comp_months} months free
                      </p>
                      <p className="text-xs text-slate-gray mt-1">
                        {invite.plan_name} access, complimentary. <strong>No credit card required.</strong> Just create your login below.
                      </p>
                      {invite.note && (
                        <p className="text-sm text-slate-gray mt-2 italic">"{invite.note}"</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleAccountNext} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-dark-slate font-medium text-sm">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                    <Input type="text" placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)}
                      className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" required />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-dark-slate font-medium text-sm">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                    <Input type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-dark-slate font-medium text-sm">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                      <Input type="password" placeholder="6+ characters" value={password} onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" required minLength={6} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-dark-slate font-medium text-sm">Confirm *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                      <Input type="password" placeholder="Confirm" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" required />
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-dark-slate font-medium text-sm">Organization / Company *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                    <Input type="text" placeholder="Your organization name" value={organization} onChange={(e) => setOrganization(e.target.value)}
                      className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" required />
                  </div>
                </div>

                {/* HBCU dropdown for Alumni Association role */}
                {selectedRole === 'alumni_association' && (
                  <div className="space-y-1.5">
                    <Label className="text-dark-slate font-medium text-sm">Institution *</Label>
                    <div className="relative">
                      <button type="button" onClick={() => setShowInstitutionDropdown(!showInstitutionDropdown)}
                        className={`w-full h-12 px-4 rounded-xl border flex items-center justify-between text-left transition-colors ${
                          institution ? 'bg-mint-fresh/10 border-emerald-300' : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                        }`}>
                        <span className={institution ? 'text-dark-slate font-medium' : 'text-slate-gray'}>{institution || 'Choose your HBCU...'}</span>
                        <ChevronDown className={`w-5 h-5 text-slate-gray transition-transform ${showInstitutionDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      {showInstitutionDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowInstitutionDropdown(false)} />
                          <div className="absolute z-50 w-full mt-2 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden">
                            <div className="p-3 border-b border-slate-100">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                                <input type="text" placeholder="Search HBCUs..." value={institutionSearch}
                                  onChange={(e) => setInstitutionSearch(e.target.value)}
                                  className="w-full h-10 pl-9 pr-4 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:border-mint-fresh" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-48 overflow-y-auto overscroll-contain">
                              {filteredInstitutions.map(inst => (
                                <button key={inst.id} type="button" onClick={() => { setInstitution(inst.name); setShowInstitutionDropdown(false); setInstitutionSearch(''); }}
                                  className="w-full px-4 py-2.5 text-left text-sm hover:bg-mint-fresh/20 flex items-center justify-between">
                                  <div><span className="text-dark-slate font-medium">{inst.name}</span><span className="text-slate-gray ml-2 text-xs">({inst.state})</span></div>
                                  {institution === inst.name && <Check className="w-4 h-4 text-emerald-600" />}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-dark-slate font-medium text-sm">Phone <span className="text-slate-gray">(optional)</span></Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
                    <Input type="tel" placeholder="(555) 123-4567" value={phone} onChange={(e) => setPhone(e.target.value)}
                      className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200" />
                  </div>
                </div>

                <label className="flex items-start space-x-3 pt-2 cursor-pointer">
                  <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-dark-slate focus:ring-dark-slate" />
                  <span className="text-sm text-slate-gray">
                    I agree to the <a href="/terms" className="text-dark-slate font-semibold hover:underline">Terms of Service</a> and{' '}
                    <a href="/privacy" className="text-dark-slate font-semibold hover:underline">Privacy Policy</a>
                  </span>
                </label>

                <div className="flex space-x-3 pt-2">
                  {!invite && (
                    <Button type="button" variant="outline" onClick={handleBack} className="h-12 rounded-full px-5">
                      <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                  )}
                  <Button type="submit" disabled={loading} className="flex-1 h-12 bg-dark-slate text-white rounded-full font-semibold">
                    {invite
                      ? (loading ? 'Creating your account…' : <>Create My Account <ArrowRight className="w-4 h-4 ml-2" /></>)
                      : <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Step 4: Payment */}
          {step === 4 && (
            <div>
              <h2 className="font-heading font-bold text-2xl text-dark-slate text-center mb-2">Complete Your Subscription</h2>
              <p className="text-slate-gray text-center text-sm mb-6">Review your selection and proceed to payment.</p>

              <div className="bg-slate-50 rounded-2xl p-6 mb-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-gray">Role</span>
                  <span className="font-semibold text-dark-slate">{currentRoleInfo?.title}</span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-gray">Plan</span>
                  <span className="font-semibold text-dark-slate">{currentPlanInfo?.name}</span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-gray">Monthly Price</span>
                  <span className="font-extrabold text-2xl text-dark-slate">
                    {currentPlanInfo?.priceLabel || `$${currentPlanInfo?.price}`}<span className="text-sm font-normal text-slate-gray">/mo</span>
                  </span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-gray">Account</span>
                  <span className="text-sm text-dark-slate">{email}</span>
                </div>
                {organization && (
                  <>
                    <div className="border-t border-slate-200" />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-gray">Organization</span>
                      <span className="text-sm text-dark-slate">{organization}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-800 text-center">
                  You will be redirected to <strong>Stripe</strong> to securely complete your payment.
                </p>
              </div>

              <div className="flex space-x-3">
                <Button variant="outline" onClick={handleBack} className="h-12 rounded-full px-5">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button onClick={handleSubscribe} disabled={loading}
                  className="flex-1 h-12 bg-dark-slate text-white rounded-full font-semibold">
                  {loading ? 'Setting up...' : 'Subscribe & Pay'}
                  {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </div>

              <p className="text-center text-xs text-slate-gray mt-4">
                Cancel anytime. Your subscription renews monthly.
              </p>
            </div>
          )}

          {/* Bottom link */}
          {step === 1 && (
            <p className="text-center text-slate-gray mt-4 text-sm">
              Already have an account? <Link to="/login" className="text-dark-slate font-semibold hover:underline">Sign In</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleSignup;
